import { Button, DatePicker, Input, MenuProps, Modal } from 'antd';
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import moment from 'moment';
const { RangePicker } = DatePicker;
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Search from '../../../components/Search/Search';
import SearchByCity from '../../../components/Search/SearchByCity';
import SearchByPhone from '../../../components/Search/SearchByPhone';
import SearchByState from '../../../components/Search/SearchByState';
import DownloadData from '../../../components/DownloadData';
import FilterModal from '../../../components/FilterModal';
import Dropdown from '../../../components/Dropdown';
import { downloadListData, downloadListDataPDF } from '../../../components/DownloadData';
import { toastError, toastSuccess } from '../../../service/Toast';
import callApi, { Method } from '../../../Api/config';
import {
  DOCUMITRA_ORDER_APPLICATION,
  DOCUMITRA_ORDER_TRANSACTION,
  PHOTO_ORDER_APPLICATION,
  PHOTO_ORDER_TRANSACTION,
  PARTNER_ORDERS,
  DOCUMITRA_LOGS,
  APPLICATION_FORM_SERVICES,
  APPLICATION_FORM_PACKAGES,
  REFUND_REQUEST,
  REJECTED_APPLICATIONS,
  AGENT_LEDGER,
  ORDER_USER,
  REFFERAL_DETAILS,
  DOCUMITRA_PARTNERS,
  PARTNER_SERVICES,
  COUPONS_MANAGEMENT,
  CONTACT_FORM,
  BECOME_PARTNER_INFO,
  PARTNER_PORTAL,
  APPLICATION_FORM_PACKAGE_DOCUMENTS,
  APPLICATION_FORM_PACKAGE_FEATURES,
  APPLICATION_FORM_QUESTIONS,
  FORMS,
  FORMS_STEPS,
  SERVICE_STEPS_ENTITY,
  SERVICE_PROCESS_STEPS,
  SERVICE_PROCESS,
  PACKAGE_PROCESS,
  AFFILIATE_PARTNERS,
  SERVICE_PROCESS_FAQ_MANAGEMENT,
  PACKAGE_PROCESS_FAQ_MANAGEMENT,
  PAYMENT_VOUCHER,
} from '../../../constants/EntityName';
import {
  getDocumitraApplicationStatusFilterOptions,
  getDocumitraGovernmentStatusOptions,
  documitraMemberTagFilterOption,
  documitraPaymentStatusFilterOption,
  statusOption as photoOrderStatus,
  partnerOrderStatusOption,
  LOGS_FILTER_DATA_MODEL,
  TRANSACTION_DATA_FILTERS,
  AGENT_LEDGER_PAYMENT_STATUS_OPTIONS,
  AGENT_LEDGER_PAYMENT_METHOD_OPTIONS,
  AGENT_LEDGER_REVIEW_STATUS_OPTIONS,
  PAYMENT_VOUCHER_STATUS_OPTIONS,
  PAYMENT_VOUCHER_METHOD_OPTIONS,
  PAYMENT_VOUCHER_ACCOUNTING_STATUS_OPTIONS,
  REFUND_STATUS_OPTIONS,
  REFUND_FEE_TYPE_OPTIONS,
  REFUND_REQUEST_PAYMENT_METHOD_OPTIONS,
  Role,
} from '../../../constants/AppConstant';
import { getRole } from '../../../service/CookieManager';
import { InvertFillButton } from '../../../components/InvertFillButton';

interface IDefaultTableHeader {
  headerName?: string;
  totalCount?: number;
  countText?: string;
  setSearchQuery: (value: any) => void;
  setPhoneSearchQuery?: (value: any) => void;
  setCitySearchQuery?: (value: any) => void;
  setStateSearchQuery?: (value: any) => void;
  onAdd?: () => void;
  addName?: string;
  hideAddPrefix?: boolean;
  disableAdd?: boolean;
  disableSearch?: boolean;
  showDownload?: boolean;
  entityName: string;
  showFilter?: boolean;
  hiddenFilterKeys?: string[];
  singleRowFilterLayout?: boolean;
  downloadActionsBelowFilters?: boolean;
  reportRedirectPath?: string;
  filterQuery?: (value: any) => void;
  items?: MenuProps['items'];
  filters?: {
    search?: string;
    status?: string;
    memberStatus?: string;
    serviceName?: string;
    paymentStatus?: string;
    plan?: string;
    addOns?: string | string[];
    [key: string]: any;
  };
  onClearFilters?: () => void;
  prefetchedFilterOptions?: {
    services?: Array<{ id: string; value: string; name: string }>;
    packages?: Array<{ id: string; value: string; name: string }>;
    countries?: Array<{ id: string; value: string; name: string }>;
  };
}

interface FilterDataModel {
  key: number;
  name: string;
  componentType: string;
  level: string;
  options?: any;
  allowMultiple?: boolean;
}

const entityNeedsServiceFilterOptions = (entityName: string) =>
  [
    APPLICATION_FORM_PACKAGES,
    DOCUMITRA_ORDER_APPLICATION,
    DOCUMITRA_ORDER_TRANSACTION,
    SERVICE_PROCESS_STEPS,
    SERVICE_PROCESS,
    SERVICE_PROCESS_FAQ_MANAGEMENT,
    PACKAGE_PROCESS_FAQ_MANAGEMENT,
    REFUND_REQUEST,
    REJECTED_APPLICATIONS,
  ].includes(entityName);

const entityNeedsPackageFilterOptions = (entityName: string) =>
  entityName === PACKAGE_PROCESS_FAQ_MANAGEMENT;

const entityNeedsCountryFilterOptions = (entityName: string) =>
  [
    PARTNER_PORTAL,
    DOCUMITRA_PARTNERS,
    DOCUMITRA_ORDER_TRANSACTION,
    AFFILIATE_PARTNERS,
    SERVICE_PROCESS_FAQ_MANAGEMENT,
    PACKAGE_PROCESS_FAQ_MANAGEMENT,
  ].includes(entityName);

const DefaultTableHeader = (props: IDefaultTableHeader) => {
  const { disableSearch, items } = props;
  const currentUserRole = getRole();
  const history = useHistory();
  const isRestrictedDownloadEntity =
    props.entityName === PARTNER_ORDERS || props.entityName === PHOTO_ORDER_APPLICATION;
  // Refund report has its own endpoint, open to Super Admin and Accountant.
  const isRefundReport = props.entityName === REFUND_REQUEST;
  const canDownloadRefundReport =
    currentUserRole === Role.SUPER_ADMIN || currentUserRole === Role.ACCOUNTANT;
  const canDownload =
    !!props.showDownload &&
    (isRefundReport
      ? canDownloadRefundReport
      : !isRestrictedDownloadEntity || currentUserRole === Role.SUPER_ADMIN);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [serviceOptions, setServiceOptions] = useState<any[]>([]);
  const [packageOptions, setPackageOptions] = useState<any[]>([]);
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<any>(props.filters || {});

  const handleDownload = async (isPDF: boolean) => {
    if (isRestrictedDownloadEntity && currentUserRole !== Role.SUPER_ADMIN) {
      toastError('Only Super Admin can download this report.');
      return;
    }

    if (isRefundReport && !canDownloadRefundReport) {
      toastError('Only Super Admin or Accountant can download this report.');
      return;
    }

    const filters = formatFilters(selectedFilters);

    // Check for date range which is required by the backend for reports
    // (optional for the refund report, which downloads the current filters as-is).
    if (!isRefundReport && (!filters.startDate || !filters.endDate)) {
      toastError('Please select a Date range filter first.');
      return;
    }

    // Map the entityName to the correct report type
    let reportTypeValue;
    if (props.entityName === DOCUMITRA_ORDER_TRANSACTION) reportTypeValue = 'DocumitraTransaction';
    else if (props.entityName === PHOTO_ORDER_TRANSACTION)
      reportTypeValue = 'PhotomitraTransaction';
    else if (props.entityName === DOCUMITRA_ORDER_APPLICATION) reportTypeValue = 'DocumitraOrder';
    else if (props.entityName === PHOTO_ORDER_APPLICATION) reportTypeValue = 'PhotomitraOrder';
    else if (props.entityName === PARTNER_ORDERS) reportTypeValue = 'PartnerOrder';
    else if (props.entityName === REFUND_REQUEST) reportTypeValue = 'RefundRequest';
    else if (props.entityName === ORDER_USER) reportTypeValue = 'Users';
    else if (props.entityName === BECOME_PARTNER_INFO) reportTypeValue = 'BecomePartnerInfo';

    if (!reportTypeValue) {
      toastError('Download not supported for this entity yet.');
      return;
    }

    toastSuccess(`Preparing ${isPDF ? 'PDF' : 'Excel'} download...`);

    const queryParams: any = isRefundReport
      ? { ...filters }
      : {
          startDate: filters.startDate,
          endDate: filters.endDate,
          reportType: reportTypeValue,
          ...filters,
        };

    try {
      const res = await callApi(
        isRefundReport ? '/v1/refund/report/download' : `/admin/download-reports`,
        'GET',
        null,
        queryParams,
        {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        Method.DOWNLOAD
      );

      if (res?.statusCode && res.statusCode >= 400) {
        const errorMessage = res.data?.message || res.data?.error || `Download failed.`;
        toastError(errorMessage);
        return;
      }

      if (res?.status && res?.data) {
        if (isPDF) {
          downloadListDataPDF(res, props.entityName);
        } else {
          downloadListData(res, props.entityName);
        }
      } else {
        toastError(`Failed to download. Please try again.`);
      }
    } catch (e) {
      toastError('Download failed.');
    }
  };

  // Define filters separately for each entity type to ensure they are unique
  const getFiltersByEntity = (): FilterDataModel[] => {
    // Partner Orders: same filter structure as Documitra (Date + Application Status + Plan + Payment Status)
    if (props.entityName === PARTNER_ORDERS) {
      return [
        {
          key: 1,
          name: 'Date',
          componentType: 'DATE',
          level: 'date',
        },
        {
          key: 11,
          name: 'Application Status',
          componentType: 'SELECT',
          level: 'memberStatus',
          allowMultiple: false,
          options: partnerOrderStatusOption,
        },
        {
          key: 13,
          name: 'Plan Name',
          componentType: 'SELECT',
          level: 'plan',
          allowMultiple: false,
          options: [
            { id: '1', name: 'Online Consultation', value: 'Online Consultation' },
            { id: '2', name: 'Ready to Ship', value: 'Ready to Ship' },
          ],
        },
      ];
    }

    const filters: FilterDataModel[] = [];

    const dateFilter: FilterDataModel = {
      key: 1,
      name: 'Date',
      componentType: 'DATE',
      level: 'date',
    };

    if (props.entityName === PARTNER_PORTAL || props.entityName === DOCUMITRA_PARTNERS) {
      filters.push(
        {
          key: 50,
          name: 'Country',
          componentType: 'SELECT',
          level: 'country',
          allowMultiple: false,
          options: countryOptions,
        },
        {
          key: 51,
          name: 'Status',
          componentType: 'SELECT',
          level: 'active',
          allowMultiple: false,
          options: [
            { id: '1', value: 'Active', name: 'Active' },
            { id: '2', value: 'Inactive', name: 'Inactive' },
            // { id: '3', value: 'Pending', name: 'Pending' },
          ],
        },
        dateFilter
      );
    }

    if (props.entityName === PARTNER_SERVICES) {
      // Only Search is required for Partner Service Management, handled by default
      return [];
    }

    if (props.entityName === APPLICATION_FORM_PACKAGES) {
      return [
        {
          key: 100,
          name: 'Service',
          componentType: 'SELECT',
          level: 'service',
          allowMultiple: false,
          options: serviceOptions,
        },
      ];
    }

    if (props.entityName === DOCUMITRA_ORDER_APPLICATION || props.entityName === PARTNER_ORDERS) {
      filters.push(dateFilter);
      filters.push(
        {
          key: 10,
          name: 'Service',
          componentType: 'SELECT',
          level: 'serviceName',
          allowMultiple: false,
          options: serviceOptions.map(opt => ({ ...opt, value: opt.name })),
        },
        {
          key: 11,
          name: 'Application Status',
          componentType: 'SELECT',
          level: 'memberStatus',
          allowMultiple: false,
          options: getDocumitraApplicationStatusFilterOptions(),
        },
        {
          key: 12,
          name: 'Payment Status',
          componentType: 'SELECT',
          level: 'paymentStatus',
          allowMultiple: false,
          options: documitraPaymentStatusFilterOption,
        }
      );

      if (props.entityName === DOCUMITRA_ORDER_APPLICATION) {
        filters.push(
          // {
          //   key: 16,
          //   name: 'Govt Status',
          //   componentType: 'SELECT',
          //   level: 'governmentStatus',
          //   allowMultiple: false,
          //   options: getDocumitraGovernmentStatusOptions(),
          // },

          {
            key: 12,
            name: 'Plan',
            componentType: 'SELECT',
            level: 'plan',
            allowMultiple: false,
            options: [
              { id: '1', name: 'Online Consultation', value: 'Online Consultation' },
              { id: '2', name: 'Ready to Ship', value: 'Ready to Ship' },
            ],
          },
          {
            key: 13,
            name: 'Add Ons',
            componentType: 'SELECT',
            level: 'addOns',
            allowMultiple: true,
            options: [
              { id: '1', name: 'Expedite', value: 'expedite' },
              { id: '2', name: 'Photo Enhancement', value: 'photoEnhancement' },
              { id: '3', name: 'Apostilled', value: 'apostilled' },
              { id: '4', name: 'Courier Service', value: 'courierService' },
            ],
          },
          {
            key: 14,
            name: 'Tag',
            componentType: 'SELECT',
            level: 'memberTag',
            allowMultiple: false,
            options: documitraMemberTagFilterOption,
          },
          {
            key: 15,
            name: 'Referral Partner ID',
            componentType: 'INPUT',
            level: 'partnerId',
          }
        );
      }
    } else if (props.entityName === PHOTO_ORDER_APPLICATION) {
      filters.push(dateFilter);
      filters.push(
        {
          key: 20,
          name: 'Order Status',
          componentType: 'SELECT',
          level: 'status',
          allowMultiple: false,
          options: photoOrderStatus,
        },
        {
          key: 21,
          name: 'Payment Status',
          componentType: 'SELECT',
          level: 'paymentStatus',
          allowMultiple: false,
          options: documitraPaymentStatusFilterOption,
        }
      );
    } else if (props.entityName === DOCUMITRA_LOGS) {
      return LOGS_FILTER_DATA_MODEL;
    } else if (
      props.entityName === DOCUMITRA_ORDER_TRANSACTION ||
      props.entityName === PHOTO_ORDER_TRANSACTION
    ) {
      const transactionFilters = [...TRANSACTION_DATA_FILTERS];
      if (props.entityName === DOCUMITRA_ORDER_TRANSACTION) {
        transactionFilters.push(
          {
            key: 30,
            name: 'Service Name',
            componentType: 'SELECT',
            level: 'serviceName',
            options: serviceOptions.map(opt => ({ ...opt, value: opt.name })),
          },
          {
            key: 31,
            name: 'Country (Applying From)',
            componentType: 'SELECT',
            level: 'applyingFrom',
            options: countryOptions,
          }
        );
      }
      return transactionFilters;
    } else if (props.entityName === REFUND_REQUEST) {
      filters.push(
        {
          key: 40,
          name: 'Fee Type',
          componentType: 'SELECT',
          level: 'refundType',
          allowMultiple: true,
          options: REFUND_FEE_TYPE_OPTIONS,
        },
        {
          key: 41,
          name: 'Service Name',
          componentType: 'SELECT',
          level: 'applicationType',
          allowMultiple: true,
          options: serviceOptions.map(opt => ({
            id: opt.id,
            value: opt.name,
            name: opt.name,
          })),
        },
        {
          key: 42,
          name: 'Payment Method',
          componentType: 'SELECT',
          level: 'paymentMethod',
          allowMultiple: true,
          options: REFUND_REQUEST_PAYMENT_METHOD_OPTIONS,
        },
        {
          key: 43,
          name: 'Status',
          componentType: 'SELECT',
          level: 'status',
          allowMultiple: true,
          options: REFUND_STATUS_OPTIONS,
        },
        dateFilter
      );
    } else if (props.entityName === REJECTED_APPLICATIONS) {
      filters.push(
        {
          key: 50,
          name: 'Service Name',
          componentType: 'SELECT',
          level: 'serviceName',
          allowMultiple: true,
          options: serviceOptions.map(opt => ({
            id: opt.id,
            value: opt.name,
            name: opt.name,
          })),
        },
        { ...dateFilter, name: 'Rejected Date' }
      );
    } else if (props.entityName === AGENT_LEDGER) {
      filters.push(dateFilter);
      filters.push(
        {
          key: 41,
          name: 'Payment Status',
          componentType: 'SELECT',
          level: 'paymentStatus',
          allowMultiple: false,
          options: AGENT_LEDGER_PAYMENT_STATUS_OPTIONS,
        },
        {
          key: 43,
          name: 'Payment Method',
          componentType: 'SELECT',
          level: 'paymentMethod',
          allowMultiple: false,
          options: AGENT_LEDGER_PAYMENT_METHOD_OPTIONS,
        },
        {
          key: 44,
          name: 'Review Status',
          componentType: 'SELECT',
          level: 'reviewStatus',
          allowMultiple: false,
          options: AGENT_LEDGER_REVIEW_STATUS_OPTIONS,
        }
      );
    } else if (props.entityName === PAYMENT_VOUCHER) {
      filters.push(
        {
          key: 61,
          name: 'Voucher Status',
          componentType: 'SELECT',
          level: 'status',
          allowMultiple: false,
          options: PAYMENT_VOUCHER_STATUS_OPTIONS,
        },
        {
          key: 62,
          name: 'Payment Method',
          componentType: 'SELECT',
          level: 'paymentMethod',
          allowMultiple: false,
          options: PAYMENT_VOUCHER_METHOD_OPTIONS,
        },
        {
          key: 63,
          name: 'Accounting Status',
          componentType: 'SELECT',
          level: 'accountingStatus',
          allowMultiple: false,
          options: PAYMENT_VOUCHER_ACCOUNTING_STATUS_OPTIONS,
        }
      );
    } else if (props.entityName === ORDER_USER) {
      // Documitra Users: registration, account, source, and order activity filters.
      filters.push(dateFilter);
      filters.push(
        {
          key: 50,
          name: 'Status',
          componentType: 'SELECT',
          level: 'active',
          allowMultiple: false,
          options: [
            { id: '1', value: 'true', name: 'Active' },
            { id: '2', value: 'false', name: 'Inactive' },
          ],
        },
        {
          key: 51,
          name: 'Source',
          componentType: 'SELECT',
          level: 'source',
          allowMultiple: false,
          options: [
            { id: '1', value: 'documitra', name: 'Documitra' },
            { id: '2', value: 'referral', name: 'Partners' },
          ],
        },
        {
          key: 52,
          name: 'Order Activity',
          componentType: 'SELECT',
          level: 'orderActivity',
          allowMultiple: false,
          options: [
            { id: '1', value: 'created', name: 'Order Created' },
            {
              id: '2',
              value: 'registeredOnly',
              name: 'Registered Only (No Orders)',
            },
          ],
        }
      );
    } else if (props.entityName === REFFERAL_DETAILS) {
      // Referral Details: Date range only (search is already present)
      filters.push(dateFilter);
    } else if (props.entityName === COUPONS_MANAGEMENT) {
      // Coupons Management: Date only as requested
      filters.push(dateFilter);
    } else if (props.entityName === SERVICE_PROCESS_STEPS || props.entityName === SERVICE_PROCESS) {
      filters.push({
        key: 70,
        name: 'Service',
        componentType: 'SELECT',
        level: 'service',
        allowMultiple: false,
        options: serviceOptions,
      });
    } else if (props.entityName === AFFILIATE_PARTNERS) {
      filters.push(
        dateFilter,
        {
          key: 80,
          name: 'Country',
          componentType: 'SELECT',
          level: 'country',
          allowMultiple: false,
          options: countryOptions,
        },
        {
          key: 81,
          name: 'Status',
          componentType: 'SELECT',
          level: 'status',
          allowMultiple: false,
          options: [
            { id: '1', value: 'active', name: 'Active' },
            { id: '2', value: 'inactive', name: 'Inactive' },
          ],
        }
      );
    } else if (props.entityName === '/affiliate/withdraw') {
      filters.push(dateFilter, {
        key: 90,
        name: 'Status',
        componentType: 'SELECT',
        level: 'status',
        allowMultiple: false,
        options: [
          { id: '1', value: 'pending', name: 'Pending' },
          { id: '2', value: 'approved', name: 'Approved' },
          { id: '3', value: 'processed', name: 'Processed' },
          { id: '4', value: 'rejected', name: 'Rejected' },
        ],
      });
    } else if (props.entityName === CONTACT_FORM || props.entityName === BECOME_PARTNER_INFO) {
      filters.push(dateFilter);
      filters.push({
        key: 60,
        name: 'Status',
        componentType: 'SELECT',
        level: 'status',
        allowMultiple: false,
        options: [
          { id: '1', value: 'Contacted', name: 'Contacted' },
          { id: '2', value: 'Closed', name: 'Closed' },
          { id: '3', value: 'Duplicate', name: 'Duplicate' },
          { id: '4', value: 'Incorrect Information', name: 'Incorrect Information' },
        ],
      });
    } else if (
      props.entityName === SERVICE_PROCESS_FAQ_MANAGEMENT ||
      props.entityName === PACKAGE_PROCESS_FAQ_MANAGEMENT
    ) {
      const isPackage = props.entityName === PACKAGE_PROCESS_FAQ_MANAGEMENT;
      if (!isPackage) {
        filters.push({
          key: 110,
          name: 'Service',
          componentType: 'SELECT',
          level: 'service',
          allowMultiple: false,
          options: serviceOptions,
        });
      } else {
        filters.push(
          {
            key: 110,
            name: 'Service',
            componentType: 'SELECT',
            level: 'service',
            allowMultiple: false,
            options: serviceOptions,
          },
          {
            key: 111,
            name: 'Package',
            componentType: 'SELECT',
            level: 'package',
            allowMultiple: false,
            options: packageOptions,
          }
        );
      }
      filters.push(
        {
          key: 112,
          name: 'Applying From',
          componentType: 'SELECT',
          level: 'applyingFrom',
          allowMultiple: false,
          options: countryOptions,
        },
        {
          key: 113,
          name: 'Citizenship',
          componentType: 'SELECT',
          level: 'citizenship',
          allowMultiple: false,
          options: countryOptions,
        },
        {
          key: 114,
          name: 'Destination',
          componentType: 'SELECT',
          level: 'destination',
          allowMultiple: false,
          options: countryOptions,
        }
      );
    }

    return filters;
  };

  const DATA_MODEL = getFiltersByEntity().filter(
    filter => !props.hiddenFilterKeys?.includes(filter.level)
  );

  function formatFilters(selectedFilters: any) {
    const formatted: any = {};
    Object.keys(selectedFilters).forEach(key => {
      const value = selectedFilters[key];
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.keys(value).length > 0
      ) {
        Object.assign(formatted, value);
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          formatted[key] = value.join(',');
        }
      } else if (value !== '' && value !== null && value !== undefined) {
        formatted[key] = value;
      }
    });
    return formatted;
  }

  const onSummitFilter = (filterKey: string, value: any) => {
    const newFilters = { ...selectedFilters, [filterKey]: value };
    setSelectedFilters(newFilters);
    if (props.filterQuery) {
      props.filterQuery(formatFilters(newFilters));
    }
  };

  const clearFilters = () => {
    setSelectedFilters({});
    props.setSearchQuery('');
    if (props.filterQuery) {
      props.filterQuery({});
    }
    if (props.onClearFilters) props.onClearFilters();
  };

  const getSearchPlaceholder = (value: string | undefined) => {
    if (value === 'Order' || value === 'Refunds') {
      return 'Order Id';
    }
    if (props.headerName === 'Partner Orders') {
      return 'Search by partner name, email, company, status...';
    }
    if (props.entityName === AGENT_LEDGER || props.headerName === 'Agent Ledger') {
      return 'Agent ID, name, email, transaction ID, order...';
    }
    if (props.entityName === PAYMENT_VOUCHER || props.headerName === 'Payment Vouchers') {
      return 'Search by Voucher ID, Order ID, Customer or Member';
    }
    if (
      props.entityName === DOCUMITRA_ORDER_APPLICATION ||
      props.entityName === DOCUMITRA_ORDER_TRANSACTION
    ) {
      return 'Search by Order ID, name, email or mobile';
    }
    if (props.entityName === REJECTED_APPLICATIONS) {
      return 'Search by Order ID, applicant, email or mobile';
    }
    return `${value?.toLowerCase()} name`;
  };

  useEffect(() => {
    const { entityName, showFilter, prefetchedFilterOptions } = props;

    if (!showFilter) {
      return;
    }

    const needsServices =
      entityNeedsServiceFilterOptions(entityName) && !prefetchedFilterOptions?.services;
    const needsPackages =
      entityNeedsPackageFilterOptions(entityName) && !prefetchedFilterOptions?.packages;
    const needsCountries =
      entityNeedsCountryFilterOptions(entityName) && !prefetchedFilterOptions?.countries;

    if (prefetchedFilterOptions?.services) {
      setServiceOptions(prefetchedFilterOptions.services);
    } else if (!needsServices) {
      setServiceOptions([]);
    }

    if (prefetchedFilterOptions?.packages) {
      setPackageOptions(prefetchedFilterOptions.packages);
    } else if (!needsPackages) {
      setPackageOptions([]);
    }

    if (prefetchedFilterOptions?.countries) {
      setCountryOptions(prefetchedFilterOptions.countries);
    } else if (!needsCountries) {
      setCountryOptions([]);
    }

    const fetchServiceOptions = async () => {
      try {
        const res = await callApi(APPLICATION_FORM_SERVICES, 'GET', null, {
          page: '1',
          limit: '100',
        });
        const results = res?.data?.data?.results || [];
        const options = [...results].reverse().map((item: any) => ({
          id: item.id,
          value: item.id,
          name: item.name,
        }));
        setServiceOptions(options);
      } catch (error) {
        console.error('Failed to fetch service options', error);
        setServiceOptions([]);
      }
    };

    const fetchCountryOptions = async () => {
      try {
        const res = await callApi('admin/countries', 'GET');
        const results = res?.data?.data || [];
        const options = results.map((country: string) => ({
          id: country,
          value: country,
          name: country,
        }));
        setCountryOptions(options);
      } catch (error) {
        console.error('Failed to fetch country options', error);
        setCountryOptions([]);
      }
    };

    const fetchPackageOptions = async () => {
      try {
        const res = await callApi(APPLICATION_FORM_PACKAGES, 'GET', null, {
          page: '1',
          limit: '500',
        });
        const results = res?.data?.data?.results || [];
        const options = results.map((item: any) => ({
          id: item.id,
          value: item.id,
          name: item.name,
        }));
        setPackageOptions(options);
      } catch (error) {
        console.error('Failed to fetch package options', error);
        setPackageOptions([]);
      }
    };

    const requests: Promise<void>[] = [];
    if (needsServices) requests.push(fetchServiceOptions());
    if (needsCountries) requests.push(fetchCountryOptions());
    if (needsPackages) requests.push(fetchPackageOptions());

    if (requests.length) {
      void Promise.all(requests);
    }
  }, [props.entityName, props.showFilter, props.prefetchedFilterOptions]);

  // Synchronize local filter state with props.filters
  useEffect(() => {
    if (props.filters) {
      const filtersFromProps: any = { ...props.filters };

      // If parent passes flat dates (startDate/endDate), wrap them for the RangePicker
      if (filtersFromProps.startDate && filtersFromProps.endDate) {
        filtersFromProps.date = {
          startDate: filtersFromProps.startDate,
          endDate: filtersFromProps.endDate,
        };
      }

      setSelectedFilters(filtersFromProps);
    } else {
      setSelectedFilters({});
    }
  }, [props.filters]);

  const isDocumitraOrderApp = props.entityName === DOCUMITRA_ORDER_APPLICATION;

  const filterSection = (
    <>
      {DATA_MODEL.filter(m => m.componentType === 'SELECT').map(filter => (
        <Dropdown
          key={filter.key}
          filterName={filter.name}
          filterKey={filter.level}
          allowMultiple={filter.allowMultiple}
          selectedValue={selectedFilters[filter.level]}
          checkBoxOptions={filter.options || []}
          onSubmitFilter={onSummitFilter}
        />
      ))}

      {DATA_MODEL.filter(m => m.componentType === 'DATE').map(filter => (
        <RangePicker
          key={filter.key}
          style={{
            height: '38px',
            borderRadius: '8px',
            background: '#f8fafc',
            border: '1px solid #dbe3ef',
            minWidth: '230px',
          }}
          value={
            selectedFilters[filter.level] &&
            selectedFilters[filter.level].startDate &&
            selectedFilters[filter.level].endDate
              ? [
                  moment(selectedFilters[filter.level].startDate),
                  moment(selectedFilters[filter.level].endDate),
                ]
              : null
          }
          onChange={dates => {
            if (dates && dates.length === 2 && dates[0] && dates[1]) {
              const value = {
                startDate: dates[0].format('YYYY-MM-DD'),
                endDate: dates[1].format('YYYY-MM-DD'),
              };
              onSummitFilter(filter.level, value);
            } else {
              onSummitFilter(filter.level, null);
            }
          }}
        />
      ))}

      {DATA_MODEL.filter(m => m.componentType === 'INPUT').map(filter => (
        <div key={filter.key} style={{ minWidth: '200px' }}>
          <Input
            allowClear
            value={selectedFilters[filter.level] || ''}
            placeholder={`Enter ${filter.name}`}
            onChange={e => onSummitFilter(filter.level, e.target.value)}
            style={{
              height: '38px',
              borderRadius: '8px',
              background: '#f8fafc',
              border: '1px solid #dbe3ef',
            }}
          />
        </div>
      ))}

      <Button
        onClick={clearFilters}
        style={{
          height: '38px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#004195',
          borderColor: '#c7d6ef',
          background: '#eef3fc',
          fontWeight: 500,
        }}
      >
        <span style={{ fontSize: '12px' }}>✕</span>
        Clear
      </Button>
    </>
  );

  const downloadAndActionsSection = (
    <>
      {canDownload && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <InvertFillButton
            variant="blue"
            fontSize="base"
            fontWeight="bold"
            style={{ font: 'bold' }}
            onClick={() => handleDownload(false)}
          >
            <FileExcelOutlined />
            Excel
          </InvertFillButton>
          <InvertFillButton
            variant="white"
            fontSize="base"
            fontWeight="semibold"
            onClick={() => handleDownload(true)}
          >
            <FilePdfOutlined />
            PDF
          </InvertFillButton>
        </div>
      )}

      {props.reportRedirectPath && (
        <InvertFillButton
          variant="blue"
          fontSize="base"
          fontWeight="bold"
          onClick={() => history.push(props.reportRedirectPath!)}
        >
          Download Report
        </InvertFillButton>
      )}

      {!props.disableAdd && (
        <InvertFillButton variant="blue" onClick={props.onAdd} fontWeight="bold">
          {props.hideAddPrefix ? props.addName : `+ Add ${props.addName}`}
        </InvertFillButton>
      )}
    </>
  );

  if (isDocumitraOrderApp) {
    const dateFilter = DATA_MODEL.find((m: any) => m.componentType === 'DATE');
    const referralFilter = DATA_MODEL.find(
      (m: any) => m.componentType === 'INPUT' && m.level === 'partnerId'
    );
    const topRowDateRange =
      dateFilter &&
      (() => {
        const filter = dateFilter as any;
        return (
          <RangePicker
            key={filter.key}
            style={{
              height: '38px',
              borderRadius: '8px',
              background: '#f8fafc',
              border: '1px solid #dbe3ef',
              minWidth: '230px',
            }}
            value={
              selectedFilters[filter.level] &&
              selectedFilters[filter.level].startDate &&
              selectedFilters[filter.level].endDate
                ? [
                    moment(selectedFilters[filter.level].startDate),
                    moment(selectedFilters[filter.level].endDate),
                  ]
                : null
            }
            onChange={dates => {
              if (dates && dates.length === 2 && dates[0] && dates[1]) {
                const value = {
                  startDate: dates[0].format('YYYY-MM-DD'),
                  endDate: dates[1].format('YYYY-MM-DD'),
                };
                onSummitFilter(filter.level, value);
              } else {
                onSummitFilter(filter.level, null);
              }
            }}
          />
        );
      })();
    const topRowReferral =
      referralFilter &&
      (() => {
        const filter = referralFilter as any;
        return (
          <div key={filter.key} style={{ minWidth: '200px' }}>
            <Input
              allowClear
              value={selectedFilters[filter.level] || ''}
              placeholder={`Enter ${filter.name}`}
              onChange={e => onSummitFilter(filter.level, e.target.value)}
              style={{
                height: '38px',
                borderRadius: '8px',
                background: '#f8fafc',
                border: '1px solid #dbe3ef',
              }}
            />
          </div>
        );
      })();
    const clearFiltersButton = (
      <Button
        onClick={clearFilters}
        style={{
          height: '38px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#004195',
          borderColor: '#c7d6ef',
          background: '#eef3fc',
          fontWeight: 500,
        }}
      >
        <span style={{ fontSize: '12px' }}>✕</span>
        Clear
      </Button>
    );
    const documitraBottomFilters = (
      <>
        {DATA_MODEL.filter(m => m.componentType === 'SELECT').map(filter => (
          <Dropdown
            key={filter.key}
            filterName={filter.name}
            filterKey={filter.level}
            allowMultiple={filter.allowMultiple}
            selectedValue={selectedFilters[filter.level]}
            checkBoxOptions={filter.options || []}
            onSubmitFilter={onSummitFilter}
          />
        ))}
        {clearFiltersButton}
      </>
    );

    if (props.downloadActionsBelowFilters) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              flexWrap: 'nowrap',
            }}
          >
            {props.showFilter && (
              <>
                {topRowDateRange}
                {DATA_MODEL.filter(m => m.componentType === 'SELECT').map(filter => (
                  <Dropdown
                    key={filter.key}
                    filterName={filter.name}
                    filterKey={filter.level}
                    allowMultiple={filter.allowMultiple}
                    selectedValue={selectedFilters[filter.level]}
                    checkBoxOptions={filter.options || []}
                    onSubmitFilter={onSummitFilter}
                  />
                ))}
                {topRowReferral}
                {clearFiltersButton}
              </>
            )}
          </div>
          <div
            style={{
              alignItems: 'center',
              borderTop: '1px solid #dbe3ef',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              paddingTop: '16px',
            }}
          >
            <span style={{ color: '#58709b', fontSize: '15px' }}>Download reports:</span>
            <span>{downloadAndActionsSection}</span>
          </div>
        </div>
      );
    }

    if (props.singleRowFilterLayout) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '0px 0px 20px 0px',
          }}
        >
          {props.showFilter && (
            <>
              {topRowDateRange}
              {DATA_MODEL.filter(m => m.componentType === 'SELECT').map(filter => (
                <Dropdown
                  key={filter.key}
                  filterName={filter.name}
                  filterKey={filter.level}
                  allowMultiple={filter.allowMultiple}
                  selectedValue={selectedFilters[filter.level]}
                  checkBoxOptions={filter.options || []}
                  onSubmitFilter={onSummitFilter}
                />
              ))}
              {topRowReferral}
              {clearFiltersButton}
            </>
          )}
          <div style={{ marginLeft: 'auto', flexShrink: 0 }}>{downloadAndActionsSection}</div>
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
          padding: '0px 0px 20px 0px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            flexWrap: 'wrap',
          }}
        >
          {!disableSearch && (
            <div style={{ flex: '1 1 auto', minWidth: '340px' }}>
              <Search
                setSearchQuery={props.setSearchQuery}
                placeholderValue={getSearchPlaceholder(props.addName)}
                leftIconImg={true}
                fullWidth={true}
              />
            </div>
          )}

          {props.showFilter && (
            <>
              {topRowDateRange}
              {topRowReferral}
            </>
          )}

          {downloadAndActionsSection}
        </div>

        {props.showFilter && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
            }}
          >
            {documitraBottomFilters}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '0px 0px 20px 0px',
      }}
    >
      {!disableSearch && (
        <div style={{ minWidth: '340px', flex: '0 1 350px' }}>
          <Search
            setSearchQuery={props.setSearchQuery}
            placeholderValue={getSearchPlaceholder(props.addName)}
            leftIconImg={true}
          />
        </div>
      )}

      {props.showFilter && filterSection}

      <div style={{ flex: '1 1 auto' }} />

      {downloadAndActionsSection}
    </div>
  );
};

DefaultTableHeader.defaultProps = {
  addName: '',
};

export default DefaultTableHeader;
